import { HomeTestIds } from '@trello/webdriverio-tests';
import { ScrolledPastWatcher } from 'common/components/scrolled-past-watcher';
import React from 'react';
import { DismissAnimation, TransitionGroup } from './dismiss-animation';
import FeedErrorBoundary from './feed-error-boundary';
import { OrientationCard } from './orientation-card';
import { SectionHeader, SectionHeaderButton } from './section-header';

import { forTemplate } from 'common/lib/util/i18n';
const l = forTemplate('home');

import styles from './up-next-section.less';

interface ItemModel {
  dismissed: boolean;
  id: string;
  pos: number;
  type: string;
  actionId: string;
}

interface UpNextModel {
  boardId: string;
  cardId: string;
  id: string;
  item: ItemModel;
  listId: string;
  orgId: string;
}

type RenderUpNext = (upNextModel: UpNextModel) => JSX.Element;

interface UpNextListProps {
  numUpNextCardsVisible: number;
  renderUpNext: RenderUpNext;
  upNextCards: UpNextModel[];
  optimisticallyDismissCard: (upNextId: string) => void;
}

const UpNextList: React.FunctionComponent<UpNextListProps> = ({
  numUpNextCardsVisible,
  renderUpNext,
  upNextCards,
  optimisticallyDismissCard,
}) => (
  <TransitionGroup
    component="ul"
    className={styles.upNextCardList}
    data-test-id={HomeTestIds.UpNextList}
  >
    {upNextCards
      .filter((upNextCard, index) => index < numUpNextCardsVisible)
      .map((upNextCard) => {
        const upNextCardId = upNextCard ? upNextCard.id : '';

        return (
          <DismissAnimation id={upNextCardId} key={upNextCardId}>
            <li key={upNextCardId} className={styles.upNextCardListItem}>
              {/* Need to put this inside of the li because if there is an error in a hidden card
              and you click "Show more" an error get's thrown in CSSTransition and crashes the page */}
              <FeedErrorBoundary
                onError={() => optimisticallyDismissCard(upNextCardId)}
              >
                {renderUpNext(upNextCard)}
              </FeedErrorBoundary>
            </li>
          </DismissAnimation>
        );
      })}
  </TransitionGroup>
);

interface UpNextSectionProps {
  dismissUpNextOrientationCard: () => void;
  numUpNextCardsVisible: number;
  onShowMoreUpNextClick: () => void;
  renderUpNext: RenderUpNext; // "connected" to modelCache, similarly to using react-redux's `connect`
  setFixedButton: () => void;
  shouldShowUpNextOrientationCard: boolean;
  showNewUpNextItems: () => void;
  showUnreadUpNextActionsButton: boolean;
  upNextCards: UpNextModel[];
  optimisticallyDismissCard: (upNextId: string) => void;
}

export class UpNextSection extends React.PureComponent<UpNextSectionProps> {
  render() {
    const {
      dismissUpNextOrientationCard,
      numUpNextCardsVisible,
      onShowMoreUpNextClick,
      renderUpNext,
      setFixedButton,
      shouldShowUpNextOrientationCard,
      showNewUpNextItems,
      showUnreadUpNextActionsButton,
      upNextCards,
      optimisticallyDismissCard,
    } = this.props;

    return (
      <div className={styles.upNextCards}>
        {!!upNextCards.length ? (
          <SectionHeader
            iconName="checkbox-checked"
            button={
              showUnreadUpNextActionsButton ? (
                <ScrolledPastWatcher setScrolledPast={setFixedButton}>
                  <SectionHeaderButton onClick={showNewUpNextItems}>
                    {l('show-new-up-next')}
                  </SectionHeaderButton>
                </ScrolledPastWatcher>
              ) : null
            }
          >
            {l('up-next')}
          </SectionHeader>
        ) : (
          <div className={styles.homeNoUpNextCards} />
        )}

        {shouldShowUpNextOrientationCard &&
          !!upNextCards.length && (
            <OrientationCard
              backgroundName="up-next"
              onDismissClick={dismissUpNextOrientationCard}
              textKey="orientation-up-next-text"
              titleKey="orientation-up-next-title"
            />
          )}

        <UpNextList
          numUpNextCardsVisible={numUpNextCardsVisible}
          renderUpNext={renderUpNext}
          upNextCards={upNextCards}
          optimisticallyDismissCard={optimisticallyDismissCard}
        />

        {upNextCards.length > numUpNextCardsVisible && (
          <DismissAnimation id="show-more">
            <a
              className="show-more u-text-align-center"
              href="#"
              onClick={onShowMoreUpNextClick}
              role="button"
            >
              {l('show-more')}
            </a>
          </DismissAnimation>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/scripts/views/home/presentational/up-next-section.tsx